<template>
  <header class="bh" :class="{ 'bh--scrolled': scrolled }">
    <div class="bh__inner">
      <!-- Left: nav link (desktop only) — wordmark already goes home, so only "The Story" is needed here -->
      <nav v-if="!isMobile" class="bh__nav bh__nav--left" aria-label="Primary">
        <a :href="storyUrl" class="bh__link">The Story</a>
      </nav>

      <!-- Mobile: hamburger button on the left -->
      <button
        v-if="isMobile"
        class="bh__menu-toggle"
        :aria-expanded="mobileOpen"
        aria-label="Open menu"
        type="button"
        @click="mobileOpen = !mobileOpen"
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <template v-if="!mobileOpen">
            <line x1="4" y1="7" x2="20" y2="7" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <line x1="4" y1="17" x2="20" y2="17" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </template>
          <template v-else>
            <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </template>
        </svg>
      </button>

      <!-- Center: logo -->
      <router-link to="/" class="bh__brand" aria-label="Banjino's home">
        <img :src="logoUrl" alt="Banjino's" class="bh__logo" />
      </router-link>

      <!-- Right: cart -->
      <div class="bh__actions">
        <router-link to="/cart" class="bh__cart" :aria-label="`Cart with ${cartCount} items`">
          <svg class="bh__cart-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M3 3h2l2.4 12.5a2 2 0 0 0 2 1.5h8.2a2 2 0 0 0 2-1.5L21 7H6"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="10" cy="20" r="1.5" fill="currentColor"/>
            <circle cx="17" cy="20" r="1.5" fill="currentColor"/>
          </svg>
          <span v-if="cartCount > 0" class="bh__cart-count" aria-hidden="true">{{ cartCount }}</span>
        </router-link>
      </div>
    </div>

    <!-- Mobile menu -->
    <transition name="bh-mobile">
      <nav v-if="mobileOpen && isMobile" class="bh__mobile" aria-label="Mobile primary">
        <router-link to="/" class="bh__mobile-link" @click="mobileOpen = false">Pre-Order</router-link>
        <a :href="storyUrl" class="bh__mobile-link">The Story</a>
        <router-link to="/cart" class="bh__mobile-link" @click="mobileOpen = false">Cart ({{ cartCount }})</router-link>
      </nav>
    </transition>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useCartStore } from '@/stores/cart'
import logoUrl from '@shared/images/black-logo-plain.svg'

const MOBILE_BREAKPOINT = 768

const cartStore = useCartStore()
const cartCount = computed(() => cartStore.itemCount || 0)

// "The Story" link points back to the marketing site (banjinos.dev or .com)
const storyUrl = computed(() => {
  if (typeof window === 'undefined') return 'https://banjinos.com'
  return window.location.hostname.includes('.dev')
    ? 'https://banjinos.dev'
    : 'https://banjinos.com'
})

const mobileOpen = ref(false)
const scrolled = ref(false)
const isMobile = ref(false)

const onScroll = () => {
  scrolled.value = window.scrollY > 16
}

const onResize = () => {
  const mobile = window.innerWidth < MOBILE_BREAKPOINT
  isMobile.value = mobile
  if (!mobile) mobileOpen.value = false
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onResize, { passive: true })
  onScroll()
  onResize()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('resize', onResize)
})
</script>

<style lang="scss" scoped>
.bh {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #285338;
  color: #ffffff;
  transition: box-shadow 200ms ease;

  &--scrolled {
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  }

  &__inner {
    max-width: 1440px;
    margin: 0 auto;
    padding: 0.85rem 1.5rem;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 1rem;
  }

  &__nav {
    display: flex;
    gap: 2rem;
    align-items: center;

    &--left {
      justify-content: flex-start;
    }
  }

  &__link {
    font-family: $font-ui;
    font-size: 0.875rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #ffffff;
    text-decoration: none;
    transition: color 150ms ease;
    position: relative;

    &:hover, &--active {
      color: #d8b767;
    }

    &--active::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -6px;
      height: 2px;
      background: #d8b767;
    }
  }

  &__brand {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    color: inherit;
    line-height: 1;
    padding: 4px 0;

    &:focus-visible {
      outline: 2px solid #d8b767;
      outline-offset: 4px;
      border-radius: 4px;
    }
  }

  &__logo {
    display: block;
    height: clamp(34px, 5vw, 46px);
    width: auto;
    // The SVG paths are black by default. Invert to render the wordmark
    // in white against the green header. CSS drop-shadow gives it a touch
    // of depth so it doesn't look pasted on.
    filter: brightness(0) invert(1) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15));
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  &__cart {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    color: #ffffff;
    background: transparent;
    text-decoration: none;
    border-radius: 50%;
    transition: background 150ms ease, color 150ms ease;

    &:hover {
      background: rgba(255, 255, 255, 0.12);
      color: #d8b767;
    }

    &:focus-visible {
      outline: 2px solid #d8b767;
      outline-offset: 2px;
    }
  }

  &__cart-icon {
    width: 24px;
    height: 24px;
  }

  &__cart-count {
    position: absolute;
    top: 2px;
    right: 2px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9999px;
    background: #EB661F;
    color: #ffffff;
    font-family: $font-ui;
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
    box-shadow: 0 0 0 2px #285338;
  }

  &__menu-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    background: transparent !important;
    border: 0;
    color: #ffffff;
    cursor: pointer;
    border-radius: 8px;
    transition: background 150ms ease;

    svg {
      width: 24px;
      height: 24px;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.12) !important;
    }

    &:focus-visible {
      outline: 2px solid #d8b767;
      outline-offset: 2px;
    }
  }

  &__mobile {
    background: #1f4329;
    padding: 0.5rem 1.5rem 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__mobile-link {
    font-family: $font-ui;
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #ffffff;
    text-decoration: none;
    padding: 0.85rem 0.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);

    &:hover, &.router-link-active {
      color: #d8b767;
    }
  }
}

.bh-mobile-enter-active,
.bh-mobile-leave-active {
  transition: max-height 250ms ease, opacity 200ms ease;
  overflow: hidden;
}
.bh-mobile-enter-from,
.bh-mobile-leave-to {
  max-height: 0;
  opacity: 0;
}
.bh-mobile-enter-to,
.bh-mobile-leave-from {
  max-height: 400px;
  opacity: 1;
}
</style>
